import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, FileSearch } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import {
  EmptyState,
  HeaderCount,
  SectionCard,
  SectionToolbar,
  TableShell,
  Th,
} from "../components/ui";
import { CreateWypisOrderModal } from "../components/wypisy/CreateWypisOrderModal";
import { ReorderWypisModal } from "../components/wypisy/ReorderWypisModal";
import { WypisStatusBadge } from "../components/wypisy/WypisStatusBadge";
import { useWypisCandidates, useWypisOrders } from "../hooks";
import { fmtDate } from "../utils";
import type {
  WypisCandidateGroup,
  WypisCandidateParcel,
  WypisOrderRow,
  WypisOrderStatus,
} from "../types";

const STATUS_FILTERS: { value: "" | "active" | WypisOrderStatus; label: string }[] = [
  { value: "active", label: "Aktywne" },
  { value: "", label: "Wszystkie" },
  { value: "fee_paid", label: "Do wysyłki" },
  { value: "sent", label: "Wysłane" },
  { value: "received", label: "Otrzymane" },
  { value: "completed", label: "Zakończone" },
];

type PaymentFilter = "" | PaymentState;

const PAYMENT_FILTERS: { value: PaymentFilter; label: string }[] = [
  { value: "", label: "Płatność: wszystkie" },
  { value: "paid", label: "Opłacone" },
  { value: "in_payment", label: "W płatności" },
  { value: "unpaid", label: "Nieopłacone" },
];

// Payment state, derived independently of the lifecycle status (orders already
// SENT stay SENT when the fee is settled afterwards). Attaching the DOO sends
// the fee out for payment automatically, so DOO present = "W płatności";
// only an uploaded payment confirmation on the fee makes the order "Opłacony".
type PaymentState = "paid" | "in_payment" | "unpaid";

const PAID_FEE_STATUSES = ["payment_uploaded", "acknowledged"];
const PENDING_FEE_STATUSES = ["pending_approval", "approved"];

const paymentState = (o: WypisOrderRow): PaymentState => {
  if (o.fee_status !== null && PAID_FEE_STATUSES.includes(o.fee_status)) return "paid";
  if (o.has_doo || (o.fee_status !== null && PENDING_FEE_STATUSES.includes(o.fee_status))) {
    return "in_payment";
  }
  return "unpaid";
};

const PAYMENT_BADGES: Record<PaymentState, { label: string; color: "green" | "yellow" | "gray" }> = {
  paid: { label: "Opłacony", color: "green" },
  in_payment: { label: "W płatności", color: "yellow" },
  unpaid: { label: "Nieopłacony", color: "gray" },
};

// Parcel search: number and obręb name; the full identifier only when the query looks
// like one ("_" / "."), otherwise "12" would hit every parcel via the TERYT unit prefix.
const parcelMatches = (p: WypisCandidateParcel, q: string) =>
  (p.short_number || p.teryt.split(".").pop() || "").toLowerCase().includes(q)
  || p.obreb.toLowerCase().includes(q)
  || (/[._]/.test(q) && p.teryt.toLowerCase().includes(q));

// Module-level panel: left — parcels needing a wypis (no owners, not in an active
// order) grouped by powiat; right — the orders with their lifecycle status.
/* Zakładka „Wypisy" strony „Zamawianie materiałów" (/power-projects/zamawianie?tab=wypisy). */
export function WypisySection() {
  const me = useAuthStore((s) => s.user);
  const { data: groups, isLoading: candidatesLoading } = useWypisCandidates();
  const { data: orders, isLoading: ordersLoading } = useWypisOrders();

  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [modalGroup, setModalGroup] = useState<WypisCandidateGroup | null>(null);
  // „Zamów ponownie wypisy” — zbiorcze zgłoszenie działek z wypisem (zmiana właścicieli);
  // wracają do kandydatów per powiat i idą do najbliższego wniosku z resztą.
  const [reorderOpen, setReorderOpen] = useState(false);
  // Powiaty domyślnie ZWINIĘTE (lista potrafi mieć kilkaset działek w kilkunastu powiatach);
  // nagłówek z licznikiem, „zaznacz wszystkie” i „Zamów wypisy” działa bez rozwijania.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // Wyszukiwarka powiatów (także po województwie i nazwie przedsięwzięcia w powiecie).
  const [countyQuery, setCountyQuery] = useState("");
  // Wyszukiwarka działek — zawęża wiersze w powiatach i rozwija powiaty z trafieniami.
  const [parcelQuery, setParcelQuery] = useState("");
  const parcelQ = parcelQuery.trim().toLowerCase();
  const visibleGroups = useMemo(() => {
    const q = countyQuery.trim().toLowerCase();
    return (groups ?? [])
      .filter(
        (g) => !q
          || g.county.toLowerCase().includes(q)
          || g.voivodeship.toLowerCase().includes(q)
          || g.parcels.some((p) => p.project_name.toLowerCase().includes(q)),
      )
      .map((group) => ({
        group,
        parcels: parcelQ ? group.parcels.filter((p) => parcelMatches(p, parcelQ)) : group.parcels,
      }))
      .filter((v) => v.parcels.length > 0);
  }, [groups, countyQuery, parcelQ]);
  const groupKey = (group: WypisCandidateGroup) => `${group.voivodeship}|${group.county}`;
  // While searching parcels every group with hits is expanded, regardless of the regular
  // (collapsed-by-default) state; collapses made during the search live separately and
  // reset with each new query, so the regular state is untouched once the search is cleared.
  const [searchCollapsed, setSearchCollapsed] = useState<Record<string, boolean>>({});
  const isExpanded = (group: WypisCandidateGroup) =>
    parcelQ ? !searchCollapsed[groupKey(group)] : !!expanded[groupKey(group)];
  const toggleExpanded = (group: WypisCandidateGroup) => {
    const key = groupKey(group);
    if (parcelQ) setSearchCollapsed((s) => ({ ...s, [key]: !s[key] }));
    else setExpanded((s) => ({ ...s, [key]: !s[key] }));
  };
  const [statusFilter, setStatusFilter] = useState<"" | "active" | WypisOrderStatus>("active");
  const [paymentFilter, setPaymentFilter] = useState<PaymentFilter>("");
  const [mineOnly, setMineOnly] = useState(false);

  const paidCount = useMemo(
    () => (orders ?? []).filter((o) => o.status === "fee_paid").length,
    [orders],
  );

  const visibleOrders = useMemo(() => {
    let rows = orders ?? [];
    if (statusFilter === "active") {
      rows = rows.filter((o) => !["completed", "cancelled"].includes(o.status));
    } else if (statusFilter) {
      rows = rows.filter((o) => o.status === statusFilter);
    }
    if (paymentFilter) rows = rows.filter((o) => paymentState(o) === paymentFilter);
    // „Moje" = założone przeze mnie albo przejęte po nieaktywnym autorze.
    if (mineOnly && me?.id) {
      rows = rows.filter(
        (o) => String(o.created_by) === String(me.id) || String(o.taken_over_by) === String(me.id),
      );
    }
    return rows;
  }, [orders, statusFilter, paymentFilter, mineOnly, me?.id]);

  const toggle = (id: string) => setChecked((s) => ({ ...s, [id]: !s[id] }));
  const groupChecked = (group: WypisCandidateGroup) => group.parcels.filter((p) => checked[p.id]);

  // Operates on the rows currently shown — with a parcel search active only on the hits,
  // so „zaznacz wszystkie” never ticks parcels hidden by the filter.
  const toggleGroup = (parcels: WypisCandidateParcel[]) => {
    const all = parcels.every((p) => checked[p.id]);
    setChecked((s) => {
      const next = { ...s };
      parcels.forEach((p) => { next[p.id] = !all; });
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <SectionToolbar
        text="Wypisy z rejestru gruntów (EGiB) — działki bez właścicieli oraz zgłoszone ręcznie („potrzebny wypis”), 1 wniosek = 1 powiat."
        actions={<HeaderCount count={paidCount} label="do wysyłki" tone="amber" />}
      />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
        {/* ── candidates ── */}
        <SectionCard
          title="Działki do zamówienia wypisu"
          action={
            <div className="flex flex-wrap items-center justify-end gap-2">
              <input
                className="input-base !py-0.5 text-xs w-36"
                placeholder="Szukaj powiatu…"
                value={countyQuery}
                onChange={(e) => setCountyQuery(e.target.value)}
                title="Filtruj powiaty po nazwie powiatu, województwa albo przedsięwzięcia."
              />
              <input
                className="input-base !py-0.5 text-xs w-36"
                placeholder="Szukaj działki…"
                value={parcelQuery}
                onChange={(e) => {
                  setParcelQuery(e.target.value);
                  setSearchCollapsed({});
                }}
                title="Filtruj działki po numerze (np. 123/4), nazwie obrębu albo pełnym identyfikatorze (np. 141201_1.0001.123/4). Zaznaczenia ukrytych działek zostają."
              />
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setReorderOpen(true)}
              title="Działki, które mają już wypis, a trzeba wziąć nowy (np. zmiana właścicieli). Wrócą tu w swoim powiecie i dołączysz je do najbliższego wniosku."
            >
              <FileSearch size={13} className="mr-1" /> Zamów ponownie wypisy…
              </Button>
            </div>
          }
        >
          {candidatesLoading ? (
            <div className="py-8 flex justify-center"><Spinner /></div>
          ) : !groups?.length ? (
            <EmptyState>
              Brak działek do zamówienia — nic bez właścicieli i bez wypisu poza aktywnymi
              zamówieniami, nic zgłoszonego ręcznie jako „potrzebny wypis” 🎉
            </EmptyState>
          ) : visibleGroups.length === 0 ? (
            <EmptyState>
              {parcelQ
                ? `Żadna działka nie pasuje do „${parcelQuery.trim()}”${countyQuery.trim() ? ` w powiatach pasujących do „${countyQuery.trim()}”` : ""}.`
                : `Żaden powiat nie pasuje do „${countyQuery.trim()}”.`}
            </EmptyState>
          ) : (
            <div className="space-y-4">
              {visibleGroups.map(({ group, parcels }) => {
                const selected = groupChecked(group);
                const open = isExpanded(group);
                return (
                  <div key={groupKey(group)} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => toggleExpanded(group)}
                          aria-expanded={open}
                          title={open ? "Zwiń powiat" : "Rozwiń powiat"}
                          className="inline-flex items-center gap-1 text-xs font-mono font-bold uppercase tracking-wide hover:text-[var(--color-primary)]"
                        >
                          {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                          Powiat {group.county}
                          {group.voivodeship ? ` · woj. ${group.voivodeship}` : ""}
                          {" "}<Badge color="gray">
                            {parcelQ ? `${parcels.length} z ${group.parcel_count}` : group.parcel_count}
                          </Badge>
                          {selected.length > 0 && <Badge color="green">zaznaczone: {selected.length}</Badge>}
                        </button>
                        <label className="flex items-center gap-1 text-[11px] cursor-pointer text-[var(--color-text-secondary)]">
                          <input
                            type="checkbox"
                            checked={parcels.every((p) => checked[p.id])}
                            onChange={() => toggleGroup(parcels)}
                          />
                          zaznacz wszystkie
                        </label>
                      </div>
                      <Button
                        size="sm"
                        disabled={selected.length === 0}
                        onClick={() => setModalGroup(group)}
                      >
                        <FileSearch size={13} className="mr-1" />
                        Zamów wypisy ({selected.length})
                      </Button>
                    </div>
                    {open && (
                    <TableShell>
                      <thead>
                        <tr>
                          <Th className="w-6"> </Th>
                          <Th>Nr działki ewidencyjnej</Th>
                          <Th>Obręb</Th>
                          <Th>Gmina</Th>
                          <Th>Przedsięwzięcie</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {parcels.map((p) => (
                          <tr
                            key={p.id}
                            className="border-t border-[var(--color-border)] hover:bg-[var(--color-bg-elevated)] cursor-pointer"
                            onClick={() => toggle(p.id)}
                          >
                            <td className="px-2 py-1">
                              <input type="checkbox" checked={!!checked[p.id]} readOnly />
                            </td>
                            <td className="px-2 py-1 font-mono">
                              {p.teryt}
                              {/* Działka z ręcznego zgłoszenia — MA już wpisanego właściciela,
                                  a wypis i tak jest potrzebny (np. do KW / potwierdzenia). */}
                              {p.wypis_needed && (p.wypisy_total > 0 || p.wypis_orders_total > 0) ? (
                                // PONOWNE zamówienie — działka MA już wypis (np. zmiana właścicieli);
                                // po wczytaniu nowego wypisu właściciele spoza niego staną się nieaktualni.
                                <span
                                  title={`Ponowne zamówienie — ${p.wypisy_total > 0 ? `działka ma już wypis (${p.wypisy_total})` : "wypis odpięty, zamówienie na działkę już było"}, wpisanych właścicieli: ${p.owners_total}.${p.wypis_needed_reason ? ` Powód: ${p.wypis_needed_reason}` : ""}`}
                                  className="ml-1 align-middle text-[9px] font-sans uppercase tracking-wide px-1 py-px border border-orange-300 bg-orange-50 text-orange-700"
                                >
                                  ponowny wypis
                                </span>
                              ) : p.wypis_needed && p.owners_total > 0 && (
                                <span
                                  title={`Zgłoszona ręcznie jako „potrzebny wypis” — działka ma już wpisanych właścicieli (${p.owners_total}).${p.wypis_needed_reason ? ` Powód: ${p.wypis_needed_reason}` : ""}`}
                                  className="ml-1 align-middle text-[9px] font-sans uppercase tracking-wide px-1 py-px border border-amber-300 bg-amber-50 text-amber-700"
                                >
                                  zgłoszona ręcznie
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-1">{p.obreb || "—"}</td>
                            <td className="px-2 py-1">{p.commune || "—"}</td>
                            <td className="px-2 py-1">
                              <Link
                                to={`/power-projects/${p.project_id}?tab=parcels`}
                                className="hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {p.project_name}
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </TableShell>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </SectionCard>

        {/* ── orders ── */}
        <SectionCard
          title="Zamówienia wypisów"
          action={
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1 text-xs cursor-pointer">
                <input type="checkbox" checked={mineOnly} onChange={() => setMineOnly((v) => !v)} />
                moje
              </label>
              <select
                className="input-base !py-0.5 text-xs"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
              >
                {STATUS_FILTERS.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
              <select
                className="input-base !py-0.5 text-xs"
                value={paymentFilter}
                onChange={(e) => setPaymentFilter(e.target.value as PaymentFilter)}
              >
                {PAYMENT_FILTERS.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
          }
        >
          {ordersLoading ? (
            <div className="py-8 flex justify-center"><Spinner /></div>
          ) : !visibleOrders.length ? (
            <EmptyState>Brak zamówień</EmptyState>
          ) : (
            <TableShell>
              <thead>
                <tr>
                  <Th>Numer</Th>
                  <Th>Powiat</Th>
                  <Th>Działki</Th>
                  <Th>Status</Th>
                  <Th>Płatność</Th>
                  <Th>Zamawiający</Th>
                  <Th>Utworzono</Th>
                </tr>
              </thead>
              <tbody>
                {visibleOrders.map((o) => (
                  <tr
                    key={o.id}
                    className={`border-t border-[var(--color-border)] hover:bg-[var(--color-bg-elevated)] ${
                      o.status === "fee_paid" ? "bg-amber-50/60" : ""
                    }`}
                  >
                    <td className="px-2 py-1.5 font-mono">
                      <Link to={`/power-projects/wypisy/${o.id}`} className="hover:underline font-bold">
                        {o.number}
                      </Link>
                    </td>
                    <td className="px-2 py-1.5">{o.county}</td>
                    <td className="px-2 py-1.5" title={o.project_names.join(", ")}>
                      {o.parcel_count}
                    </td>
                    <td className="px-2 py-1.5"><WypisStatusBadge status={o.status} /></td>
                    <td className="px-2 py-1.5">
                      <Badge color={PAYMENT_BADGES[paymentState(o)].color}>
                        {PAYMENT_BADGES[paymentState(o)].label}
                      </Badge>
                    </td>
                    <td className="px-2 py-1.5">{o.created_by_name}</td>
                    <td className="px-2 py-1.5">{fmtDate(o.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </TableShell>
          )}
        </SectionCard>
      </div>

      {reorderOpen && <ReorderWypisModal onClose={() => setReorderOpen(false)} />}
      {modalGroup && (
        <CreateWypisOrderModal
          group={modalGroup}
          parcels={groupChecked(modalGroup)}
          onClose={() => setModalGroup(null)}
        />
      )}
    </div>
  );
}
